import { matchesVerbForm, normalizeVerbForm } from './lib/verbs';
import type { TrainingResultItem } from './lib/trainingSession';
import { allVerbs, getAcceptedVerbForms } from './services/verbs';
import type { IrregularVerb, TrainingMode } from './types';

export const MISTAKES_STORAGE_KEY = 'verbmaster.mistakes.v1';
export const MISTAKES_UPDATED_EVENT = 'verbmaster:mistakes-updated';
export const MISTAKE_RETRAINING_SESSION_SIZE = 10;

type FormType = 'pastSimple' | 'pastParticiple';
type MistakeType = 'typing' | 'choice' | 'flashcard' | 'speed';

export interface JournalMistake {
  id: string;
  userId: string;
  verbId: string;
  infinitive: string;
  pastSimple: string;
  pastParticiple: string;
  translation: string;
  level: string;
  userAnswer: {
    pastSimple?: string;
    pastParticiple?: string;
  };
  correctAnswer: {
    pastSimple: string;
    pastParticiple: string;
  };
  mistakeType: MistakeType;
  mistakeCount: number;
  lastMistake: string;
  mastered: boolean;
  nextReview: string;
  updatedAt: string;
}

interface RecordTrainingMistakesInput {
  userId: string;
  mode: TrainingMode;
  results: TrainingResultItem[];
}

interface FetchMistakeTrainingVerbsOptions {
  includeMastered?: boolean;
  limit?: number;
}

interface RecordTrainingMistakesResult {
  changed: boolean;
  mistakes: JournalMistake[];
}

function canUseStorage() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function notifyMistakesUpdated() {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(new CustomEvent(MISTAKES_UPDATED_EVENT));
}

function toMistakeType(mode: TrainingMode): MistakeType {
  if (mode === 'multiple-choice') {
    return 'choice';
  }

  if (mode === 'flashcards') {
    return 'flashcard';
  }

  if (mode === 'speed-verbs') {
    return 'speed';
  }

  return 'typing';
}

function safeParse(value: string | null) {
  if (!value) {
    return [] as JournalMistake[];
  }

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? normalizeMistakeList(parsed) : [];
  } catch {
    return [] as JournalMistake[];
  }
}

function sortMistakes(mistakes: JournalMistake[]) {
  return [...mistakes].sort((left, right) => {
    const updatedDiff = getUpdatedAtTime(right) - getUpdatedAtTime(left);
    if (updatedDiff !== 0) {
      return updatedDiff;
    }

    return left.id.localeCompare(right.id);
  });
}

function serializeMistakes(mistakes: JournalMistake[]) {
  return JSON.stringify(sortMistakes(mistakes));
}

function normalizeMistake(input: unknown): JournalMistake | null {
  if (!input || typeof input !== 'object') {
    return null;
  }

  const source = input as Partial<JournalMistake>;
  const userId = typeof source.userId === 'string' ? source.userId : '';
  const verbId = typeof source.verbId === 'string' ? source.verbId : '';

  if (!userId || !verbId) {
    return null;
  }

  const now = new Date().toISOString();
  const lastMistake = typeof source.lastMistake === 'string' ? source.lastMistake : now;
  const updatedAt = typeof source.updatedAt === 'string' ? source.updatedAt : lastMistake;

  return {
    id: typeof source.id === 'string' && source.id ? source.id : `${userId}:${verbId}`,
    userId,
    verbId,
    infinitive: typeof source.infinitive === 'string' ? source.infinitive : '',
    pastSimple: typeof source.pastSimple === 'string' ? source.pastSimple : '',
    pastParticiple: typeof source.pastParticiple === 'string' ? source.pastParticiple : '',
    translation: typeof source.translation === 'string' ? source.translation : '',
    level: typeof source.level === 'string' ? source.level : '',
    userAnswer: {
      pastSimple:
        source.userAnswer && typeof source.userAnswer.pastSimple === 'string'
          ? source.userAnswer.pastSimple
          : undefined,
      pastParticiple:
        source.userAnswer && typeof source.userAnswer.pastParticiple === 'string'
          ? source.userAnswer.pastParticiple
          : undefined,
    },
    correctAnswer: {
      pastSimple:
        source.correctAnswer && typeof source.correctAnswer.pastSimple === 'string'
          ? source.correctAnswer.pastSimple
          : '',
      pastParticiple:
        source.correctAnswer && typeof source.correctAnswer.pastParticiple === 'string'
          ? source.correctAnswer.pastParticiple
          : '',
    },
    mistakeType:
      source.mistakeType === 'choice' ||
      source.mistakeType === 'flashcard' ||
      source.mistakeType === 'speed'
        ? source.mistakeType
        : 'typing',
    mistakeCount:
      typeof source.mistakeCount === 'number' && Number.isFinite(source.mistakeCount)
        ? Math.max(0, Math.round(source.mistakeCount))
        : 0,
    lastMistake,
    mastered: source.mastered === true,
    nextReview: typeof source.nextReview === 'string' ? source.nextReview : lastMistake,
    updatedAt,
  };
}

function normalizeMistakeList(input: unknown) {
  if (!Array.isArray(input)) {
    return [] as JournalMistake[];
  }

  return input
    .map((item) => normalizeMistake(item))
    .filter((item): item is JournalMistake => Boolean(item));
}

function getUpdatedAtTime(mistake: JournalMistake) {
  const timestamp = new Date(mistake.updatedAt || mistake.lastMistake).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

function mergeMistakeCollections(...collections: JournalMistake[][]) {
  const mistakesById = new Map<string, JournalMistake>();

  collections.forEach((items) => {
    items.forEach((item) => {
      const normalized = normalizeMistake(item);
      if (!normalized) {
        return;
      }

      const existing = mistakesById.get(normalized.id);
      if (!existing || getUpdatedAtTime(normalized) >= getUpdatedAtTime(existing)) {
        mistakesById.set(normalized.id, normalized);
      }
    });
  });

  return sortMistakes(Array.from(mistakesById.values()));
}

function readAllMistakes() {
  if (!canUseStorage()) {
    return [] as JournalMistake[];
  }

  return safeParse(window.localStorage.getItem(MISTAKES_STORAGE_KEY));
}

function writeAllMistakes(mistakes: JournalMistake[]) {
  if (!canUseStorage()) {
    return;
  }

  window.localStorage.setItem(MISTAKES_STORAGE_KEY, serializeMistakes(mistakes));
  notifyMistakesUpdated();
}

function writeAllMistakesIfChanged(mistakes: JournalMistake[]) {
  const current = readAllMistakes();
  if (serializeMistakes(current) === serializeMistakes(mistakes)) {
    return false;
  }

  writeAllMistakes(mistakes);
  return true;
}

function addDaysIso(days: number) {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString();
}

function getNextReviewDate(mistakeCount: number) {
  if (mistakeCount >= 5) {
    return addDaysIso(7);
  }

  if (mistakeCount >= 3) {
    return addDaysIso(3);
  }

  return addDaysIso(1);
}

function splitCombinedAnswer(value?: string) {
  const parts = (value ?? '')
    .split('/')
    .map((part) => part.trim())
    .filter((part) => part !== '-');

  return {
    pastSimple: parts[0] ?? '',
    pastParticiple: parts[1] ?? '',
  };
}

function findVerb(result: TrainingResultItem): IrregularVerb | undefined {
  const normalizedId = normalizeVerbForm(result.id);
  const normalizedLabel = normalizeVerbForm(result.label);

  return allVerbs.find((verb) => {
    return normalizeVerbForm(verb.id) === normalizedId || normalizeVerbForm(verb.infinitive) === normalizedLabel;
  });
}

function inferFormType(verb: IrregularVerb, result: TrainingResultItem): FormType {
  const expected = result.correctAnswer ?? '';
  const matchesPastSimple = matchesVerbForm(expected, getAcceptedVerbForms(verb, 'pastSimple'));
  const matchesPastParticiple = matchesVerbForm(expected, getAcceptedVerbForms(verb, 'pastParticiple'));

  if (matchesPastSimple && !matchesPastParticiple) {
    return 'pastSimple';
  }

  if (matchesPastParticiple && !matchesPastSimple) {
    return 'pastParticiple';
  }

  const prompt = (result.prompt ?? '').toLowerCase();
  return prompt.includes('participle') ? 'pastParticiple' : 'pastSimple';
}

function createMistakeEntry(userId: string, verb: IrregularVerb, mode: TrainingMode): JournalMistake {
  const now = new Date().toISOString();

  return {
    id: `${userId}:${verb.id}`,
    userId,
    verbId: verb.id,
    infinitive: verb.infinitive,
    pastSimple: verb.pastSimple,
    pastParticiple: verb.pastParticiple,
    translation: verb.translation,
    level: verb.level,
    userAnswer: {},
    correctAnswer: {
      pastSimple: verb.pastSimple,
      pastParticiple: verb.pastParticiple,
    },
    mistakeType: toMistakeType(mode),
    mistakeCount: 0,
    lastMistake: now,
    mastered: false,
    nextReview: addDaysIso(1),
    updatedAt: now,
  };
}

function upsertMistake(
  mistakes: JournalMistake[],
  userId: string,
  verb: IrregularVerb,
  mode: TrainingMode,
  updates: Partial<JournalMistake['userAnswer']>,
) {
  const existing = mistakes.find((item) => item.userId === userId && item.verbId === verb.id);
  const target = existing ?? createMistakeEntry(userId, verb, mode);
  const now = new Date().toISOString();

  target.mistakeType = toMistakeType(mode);
  target.userAnswer = {
    ...target.userAnswer,
    ...updates,
  };
  target.correctAnswer = {
    pastSimple: verb.pastSimple,
    pastParticiple: verb.pastParticiple,
  };
  target.lastMistake = now;
  target.mastered = false;
  target.mistakeCount += 1;
  target.nextReview = getNextReviewDate(target.mistakeCount);
  target.updatedAt = now;

  if (!existing) {
    mistakes.push(target);
  }
}

function registerSuccessfulAttempt(mistakes: JournalMistake[], userId: string, verbId: string) {
  const existing = mistakes.find((item) => item.userId === userId && item.verbId === verbId);
  if (!existing || existing.mastered) {
    return;
  }

  const now = new Date().toISOString();

  if (existing.mistakeCount <= 1) {
    existing.mistakeCount = 0;
    existing.mastered = true;
    existing.nextReview = now;
    existing.updatedAt = now;
    return;
  }

  existing.mistakeCount -= 1;
  existing.nextReview = getNextReviewDate(existing.mistakeCount);
  existing.updatedAt = now;
}

function processTypingResult(mistakes: JournalMistake[], userId: string, verb: IrregularVerb, result: TrainingResultItem) {
  if (result.correct) {
    registerSuccessfulAttempt(mistakes, userId, verb.id);
    return;
  }

  const userForms = splitCombinedAnswer(result.userAnswer);
  const correctForms = splitCombinedAnswer(result.correctAnswer);

  const expectedPastSimple = correctForms.pastSimple
    ? [correctForms.pastSimple, ...getAcceptedVerbForms(verb, 'pastSimple')]
    : getAcceptedVerbForms(verb, 'pastSimple');
  const expectedPastParticiple = correctForms.pastParticiple
    ? [correctForms.pastParticiple, ...getAcceptedVerbForms(verb, 'pastParticiple')]
    : getAcceptedVerbForms(verb, 'pastParticiple');

  if (userForms.pastSimple && !matchesVerbForm(userForms.pastSimple, expectedPastSimple)) {
    upsertMistake(mistakes, userId, verb, 'typing', { pastSimple: userForms.pastSimple });
  }

  if (userForms.pastParticiple && !matchesVerbForm(userForms.pastParticiple, expectedPastParticiple)) {
    upsertMistake(mistakes, userId, verb, 'typing', { pastParticiple: userForms.pastParticiple });
  }
}

function processChoiceResult(mistakes: JournalMistake[], userId: string, verb: IrregularVerb, result: TrainingResultItem) {
  if (result.correct) {
    registerSuccessfulAttempt(mistakes, userId, verb.id);
    return;
  }

  const formType = inferFormType(verb, result);
  upsertMistake(
    mistakes,
    userId,
    verb,
    'multiple-choice',
    formType === 'pastSimple'
      ? { pastSimple: result.userAnswer ?? '' }
      : { pastParticiple: result.userAnswer ?? '' },
  );
}

function processFlashcardResult(mistakes: JournalMistake[], userId: string, verb: IrregularVerb, result: TrainingResultItem) {
  if (result.correct) {
    registerSuccessfulAttempt(mistakes, userId, verb.id);
    return;
  }

  upsertMistake(mistakes, userId, verb, 'flashcards', {});
}

function processSpeedResult(mistakes: JournalMistake[], userId: string, verb: IrregularVerb, result: TrainingResultItem) {
  if (result.correct) {
    registerSuccessfulAttempt(mistakes, userId, verb.id);
    return;
  }

  upsertMistake(mistakes, userId, verb, 'speed-verbs', {});
}

export function recordTrainingMistakes({ userId, mode, results }: RecordTrainingMistakesInput): RecordTrainingMistakesResult {
  if (!userId || !Array.isArray(results) || results.length === 0) {
    return {
      changed: false,
      mistakes: fetchMistakes(userId),
    };
  }

  const existingMistakes = readAllMistakes();
  const mistakes = mergeMistakeCollections(existingMistakes);

  results.forEach((result) => {
    const verb = findVerb(result);
    if (!verb) {
      return;
    }

    if (mode === 'typing') {
      processTypingResult(mistakes, userId, verb, result);
      return;
    }

    if (mode === 'multiple-choice') {
      processChoiceResult(mistakes, userId, verb, result);
      return;
    }

    if (mode === 'speed-verbs') {
      processSpeedResult(mistakes, userId, verb, result);
      return;
    }

    processFlashcardResult(mistakes, userId, verb, result);
  });

  const nextMistakes = sortMistakes(mistakes);
  const changed = writeAllMistakesIfChanged(nextMistakes);

  return {
    changed,
    mistakes: nextMistakes.filter((item) => item.userId === userId),
  };
}

export function fetchMistakes(userId: string) {
  return readAllMistakes()
    .filter((item) => item.userId === userId)
    .sort((left, right) => new Date(right.lastMistake).getTime() - new Date(left.lastMistake).getTime());
}

export function getMistakesForCloud(userId: string) {
  return fetchMistakes(userId).map((mistake) => ({
    ...mistake,
    updatedAt: mistake.updatedAt || mistake.lastMistake,
  }));
}

export function hydrateMistakesFromCloud(userId: string, cloudValue: unknown) {
  if (!userId) {
    return;
  }

  const remoteMistakes = normalizeMistakeList(cloudValue).filter((item) => item.userId === userId);
  if (remoteMistakes.length === 0) {
    return;
  }

  const localMistakes = readAllMistakes();
  const otherUsers = localMistakes.filter((item) => item.userId !== userId);
  const mergedUserMistakes = mergeMistakeCollections(
    localMistakes.filter((item) => item.userId === userId),
    remoteMistakes,
  );

  writeAllMistakesIfChanged([...otherUsers, ...mergedUserMistakes]);
}

function isReviewReady(mistake: JournalMistake) {
  return new Date(mistake.nextReview).getTime() <= Date.now();
}

function compareMistakesForTraining(left: JournalMistake, right: JournalMistake) {
  const leftReady = isReviewReady(left) ? 0 : 1;
  const rightReady = isReviewReady(right) ? 0 : 1;

  if (leftReady !== rightReady) {
    return leftReady - rightReady;
  }

  if (left.mistakeCount !== right.mistakeCount) {
    return right.mistakeCount - left.mistakeCount;
  }

  return getUpdatedAtTime(right) - getUpdatedAtTime(left);
}

function resolveMistakeVerb(mistake: JournalMistake) {
  const normalizedVerbId = normalizeVerbForm(mistake.verbId);
  const normalizedInfinitive = normalizeVerbForm(mistake.infinitive);

  return allVerbs.find((verb) => {
    return normalizeVerbForm(verb.id) === normalizedVerbId || normalizeVerbForm(verb.infinitive) === normalizedInfinitive;
  });
}

export function fetchMistakeTrainingVerbs(
  userId: string,
  { includeMastered = false, limit }: FetchMistakeTrainingVerbsOptions = {},
) {
  const seen = new Set<string>();

  return fetchMistakes(userId)
    .filter((mistake) => includeMastered || !mistake.mastered)
    .sort(compareMistakesForTraining)
    .map((mistake) => resolveMistakeVerb(mistake))
    .filter((verb): verb is IrregularVerb => Boolean(verb))
    .filter((verb) => {
      if (seen.has(verb.id)) {
        return false;
      }

      seen.add(verb.id);
      return true;
    })
    .slice(0, typeof limit === 'number' ? Math.max(0, limit) : undefined);
}
